import type { AriaRole, ComponentPropsWithoutRef, ReactNode } from 'react';

const PHONE_DISPLAY = '+1-917-753-7754';
const PHONE_HREF = 'tel:+19177537754';
const EMAIL_DISPLAY = 'dev@intelliolabs.com';
const EMAIL_HREF = 'mailto:dev@intelliolabs.com';

type EngineeringSupportBannerProps = {
  className?: string;
  compact?: boolean;
};

const joinClassNames = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

export const EngineeringSupportBanner = ({
  className,
  compact = false,
}: EngineeringSupportBannerProps) => (
  <aside
    data-testid="intellio-engineering-support-banner"
    className={joinClassNames(
      'rounded-2xl border border-primary/25 bg-primary/10 text-irongrey shadow-sm',
      compact ? 'px-3 py-2' : 'px-4 py-3',
      className,
    )}
  >
    <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-primary">
      Need help?
    </p>
    <div className={joinClassNames('font-semibold leading-5', compact ? 'mt-1 text-xs' : 'mt-2 text-sm')}>
      <p>
        <span>For urgent help, call or text Intellio support at </span>
        <a className="font-extrabold text-strawberry underline decoration-strawberry/30 underline-offset-2" href={PHONE_HREF}>
          {PHONE_DISPLAY}
        </a>
      </p>
      <p className="mt-1">
        <span>For non-urgent help, email </span>
        <a className="font-extrabold text-primary underline decoration-primary/30 underline-offset-2" href={EMAIL_HREF}>
          {EMAIL_DISPLAY}
        </a>
      </p>
    </div>
  </aside>
);

type SupportErrorMessageProps = {
  children: ReactNode;
  role?: AriaRole | null;
  showSupport?: boolean;
  technicalDetails?: ReactNode;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'role'>;

export const SupportErrorMessage = ({
  children,
  className,
  role = 'alert',
  showSupport = true,
  technicalDetails,
  ...divProps
}: SupportErrorMessageProps) => (
  <div
    {...divProps}
    role={role ?? undefined}
    className={joinClassNames(
      'rounded-2xl border border-strawberry/20 bg-strawberry/5 px-4 py-3 text-sm font-semibold text-strawberry',
      className,
    )}
  >
    {showSupport ? <EngineeringSupportBanner compact className="mb-3" /> : null}
    <div>{children}</div>
    {technicalDetails ? (
      <details className="mt-3 rounded-lg border border-strawberry/15 bg-white/70 px-3 py-2 text-xs text-irongrey/70">
        <summary className="cursor-pointer font-bold text-irongrey/70">
          Show technical details
        </summary>
        <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-5">
          {technicalDetails}
        </pre>
      </details>
    ) : null}
  </div>
);
